import { useState } from 'react';
import styled from '@emotion/styled';
import Link from 'next/link';

import { categories } from '@/components/blocks/CommonProgramme';
import { screen } from '@/components/common/breakpoints';
import { Filters } from '@/components/common/Filters';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import {
  Heading,
  HeadingLevel,
  List,
  Paragraph,
} from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { useWatchMedia } from '@/lib/useWatchMedia';
import HexagonLogo from '@/public/images/hexagonLogo.svg';

import type {
  SanityProgrammeCatalogueType,
  SanityProgrammeType,
} from '@/types/sanity';
import type { MouseEvent } from 'react';

export interface ProgrammeCatalogueProps extends SanityProgrammeCatalogueType {
  programmes: SanityProgrammeType[];
}

const ProgrammeCatalogue = ({
  heading,
  programmes,
}: ProgrammeCatalogueProps) => {
  const [filterBy, setFilterBy] = useState<string>('All');
  const handleFiltersValue = (e: MouseEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      setFilterBy(e.target.value);
    }
  };

  useWatchMedia(screen.mdQuery, () => setFilterBy('All'));

  return (
    <Section backgroundColour="--background-warm-base" verticalPadding>
      <div id="catalogue">
        <Header>
          <StyledLogo aria-hidden="true" />
          <HeadingLevel>
            <Heading size="caption-sm">{heading}</Heading>
          </HeadingLevel>
          <Categories>
            <Filters
              label="Filter by programme category"
              groupName="programme catalogue"
              options={categories(programmes)}
              onChange={handleFiltersValue}
              filterBy={filterBy}
            />
          </Categories>
        </Header>
        <ProgrammeCards filterBy={filterBy} programmes={programmes} />
      </div>
    </Section>
  );
};

const StyledLogo = styled(HexagonLogo)`
  width: 88px;
  margin-bottom: var(--spacing-300);
  path {
    fill: var(--surface-inverse-primary);
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Categories = styled.div`
  display: flex;
  margin-top: var(--spacing-600);
`;

const ProgrammeCards = ({ filterBy, programmes }) => {
  const filteredProgrammes =
    filterBy === 'All'
      ? programmes
      : programmes.filter((p) => p.category === filterBy);

  return (
    <ProgrammeCardColumns>
      {[0, 1, 2].map((column) => (
        <ProgrammeCardColumn key={column}>
          {filteredProgrammes
            .filter((_, idx) => idx % 3 === column)
            .map((p) => (
              <ProgrammeCard programme={p} key={p._id} />
            ))}
        </ProgrammeCardColumn>
      ))}
    </ProgrammeCardColumns>
  );
};

const ProgrammeCardColumns = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--spacing-800);

  ${screen.md} {
    flex-direction: row;
  }
`;

const ProgrammeCardColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: calc(var(--spacing-300) * -1);

  ${screen.md} {
    flex-direction: column;
    flex: 1;

    & + & {
      margin-left: var(--spacing-400);
    }
  }
`;

const programmePath = (region, slug) => {
  switch (region) {
    case 'en-GB':
      return `/programmes/${slug}`;
    case 'en-US':
      return `/programs/${slug}`;
    default:
      return `/programmes/${slug}`;
  }
};

const ProgrammeCard = ({ programme }) => {
  return (
    <CardContainer
      locale={programme.region}
      href={programmePath(programme.region, programme.slug.current)}
    >
      <HeadingLevelProvider level={3}>
        <ProgrammeCardBox>
          {programme.icon?.asset && (
            <IconWrapper>
              <Image {...programme.icon} sizes="110px" />
            </IconWrapper>
          )}
          <Heading size="headline-sm">{programme.name}</Heading>
          <DurationAndLevel>
            <Paragraph size="caption-lg">{programme.duration}</Paragraph>
            {!!programme.level && (
              <Paragraph size="caption-lg">Level {programme.level}</Paragraph>
            )}
          </DurationAndLevel>

          <ShortSummary size="body-md">{programme.shortSummary}</ShortSummary>
          <HeadingLevel>
            <WhoItsFor size="caption-sm">WHO IT&apos;S FOR</WhoItsFor>
          </HeadingLevel>
          <Paragraph size="body-md">{programme.whoItsFor}</Paragraph>

          {!!programme.qualificationsReceived && (
            <WhatYouGetBox>
              <HeadingLevel>
                <Heading size="caption-sm">WHAT YOU GET</Heading>
              </HeadingLevel>
              <List size="body-md">
                {programme.qualificationsReceived.map((qr) => (
                  <li key={qr}>
                    <StyledHexagon size="16px" icon="Hexagon" />
                    {qr}
                  </li>
                ))}
              </List>
            </WhatYouGetBox>
          )}
        </ProgrammeCardBox>
      </HeadingLevelProvider>
    </CardContainer>
  );
};

const CardContainer = styled(Link)`
  text-decoration: none;
  color: inherit;
  border-radius: var(--radius-s);
  background-color: var(--surface-subtle);
  padding: var(--spacing-300);
  margin-bottom: var(--spacing-300);
  cursor: pointer;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.03);
  }
`;

const IconWrapper = styled.div`
  width: 110px;
  margin-bottom: var(--spacing-300);
`;

const ProgrammeCardBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const DurationAndLevel = styled.div`
  display: flex;
  color: var(--text-action);
  margin-top: var(--spacing-300);
  margin-bottom: var(--spacing-100);
  > * {
    margin-right: var(--spacing-300);
    margin-bottom: var(--spacing-50);
  }

  ${screen.md} {
    display: block;
  }
`;

const ShortSummary = styled(Paragraph)`
  margin-bottom: var(--spacing-300);
`;

const WhoItsFor = styled(Heading)`
  margin-bottom: var(--spacing-xx-small);
`;

const WhatYouGetBox = styled.div`
  background-color: var(--surface-primary-subtle);
  border-radius: var(--radius-s);
  padding: var(--spacing-300);
  margin-top: var(--spacing-300);

  ${Heading} {
    margin-bottom: var(--spacing-xx-small);
  }

  li {
    list-style: none;
    margin-left: 24px;
    position: relative;
    display: flex;
    align-items: top;
  }
`;

const StyledHexagon = styled(Icon)`
  position: absolute;
  left: -24px;
  top: 4px;
  > svg {
    top: -3px;
    position: relative;
  }
`;

export { ProgrammeCatalogue };
